/**
 * Publieke uitleg: tekening/PDF → voorstel → goedkeuring (Fase 3 — gebruikershulp).
 */
import { Link } from 'react-router-dom';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';

export function HelpTekeningFlow() {
  return (
    <div className="animate-page-enter">
      <SeoMeta
        path="/help/tekening"
        title="Tekening en PDF in je dossier"
        description="Hulp CalqTool™: documentenbibliotheek per dossier, tekening- en PDF-analyse, posten in delen importeren en goedkeuren op Posten — geen stille overschrijving."
      />
      <AnimatedSection delay={0.05}>
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Tekening & PDF' }]} />
        <h1 className="mb-4 text-2xl font-bold text-text">Tekening of PDF in je dossier</h1>
        <p className="mb-4 max-w-2xl text-sm text-text-muted">
          CalqTool maakt van een upload <strong className="text-text">geen</strong> automatische begroting. Je krijgt
          eerst een <strong className="text-text">voorstel</strong>; pas na jouw controle en expliciete goedkeuring
          komen de posten vast in je prijsboek.
        </p>
        <p className="mb-8 max-w-2xl rounded-lg border border-border/80 bg-surface px-3 py-2 text-xs text-text-muted">
          Dit is een <strong className="text-text">handleiding</strong> (zoals in de footer onder Handleidingen). Loopt
          de site zelf niet? Zie <Link to="/status" className="font-medium text-primary hover:underline">dienststatus</Link>{' '}
          en <Link to="/troubleshoot" className="font-medium text-primary hover:underline">problemen oplossen</Link>.
        </p>

        <ol className="mb-10 max-w-2xl list-decimal space-y-4 pl-5 text-sm text-text-muted">
          <li>
            <strong className="text-text">Gegevens kloppen</strong> — Op tab <strong>Gegevens</strong>: projecttype,
            gebouw, <strong className="text-text">klanttype</strong> (importadvies en toon; de technische scan is voor iedereen even diep), regio/postcode.
            Sla op vóór je analyse start.
          </li>
          <li>
            <strong className="text-text">Documentenbibliotheek</strong> — Bestanden die je uploadt voor analyse komen ook
            in je dossier-bibliotheek. Geef ze een <strong className="text-text">duidelijke naam</strong> (bij upload of
            met het potlood): bijv. bestaand/ontwerp, verdieping, sloop/installatie. Onder &quot;Tekening analyseren&quot;
            kun je <strong className="text-text">bestaande bibliotheekdocumenten aanvinken</strong> voor een nieuwe run —
            je hoeft ze niet opnieuw te uploaden.
          </li>
          <li>
            <strong className="text-text">Analyseren</strong> — Je ziet een samenvatting, betrouwbaarheid en voorgestelde
            koppelingen met het prijsboek. Het resultaat blijft op het dossier staan (ook na verversen) tot je opnieuw
            analyseert, de analyse wist, of alle voorgestelde posten hebt geïmporteerd.
          </li>
          <li>
            <strong className="text-text">Importeren (bulk of per post)</strong> — Gebruik het <strong className="text-text">
              importfilter
            </strong>{' '}
            (minimaal kern, kern+aanbevolen, of alles) om snel te kiezen wat bij je klanttype past; je kunt daarna nog
            per regel aanvinken. Zet het voorstel op het dossier. Je kunt in <strong className="text-text">meerdere stappen</strong>{' '}
            importeren: niet-gekozen posten blijven in je opgeslagen analyse. Het conceptvoorstel wordt <strong className="text-text">
              aangevuld
            </strong>
            .
          </li>
          <li>
            <strong className="text-text">Posten</strong> — Controleer en pas aan; daarna goedkeuren wanneer je klaar
            bent.
          </li>
          <li>
            <strong className="text-text">Al bestaande posten?</strong> — Op Posten kies je vóór goedkeuren:{' '}
            <strong className="text-text">toevoegen</strong> (voorstel erbij), <strong className="text-text">vervangen</strong>{' '}
            (hele begroting = voorstel) of <strong className="text-text">samenvoegen</strong> (zelfde systeem+variant+locatie
            → aantallen optellen). Undo/redo op Posten geldt voor je <em>lokale</em> bewerkingen tot je opslaat; na goedkeuren
            komt de nieuwe serverstand in je editor.
          </li>
        </ol>

        <p className="mb-8 max-w-2xl rounded-lg border border-border/80 bg-surface-card/80 px-4 py-3 text-xs text-text-muted">
          <strong className="text-text">Limiet:</strong> maximaal tien platen per analyse (PDF-pagina’s tellen mee). Kies
          minder documenten of splits in twee analyses.
        </p>

        <p className="mb-8 max-w-2xl rounded-lg border border-border/80 bg-surface px-4 py-3 text-xs text-text-muted">
          <strong className="text-text">Prijsboekversie per dossier:</strong> elk dossier is bij aanmaak{' '}
          <strong className="text-text">vastgezet op een catalogusversie</strong> (herleidbare begroting op Posten,
          Resultaat en export). De tekeninganalyse gebruikt de <strong className="text-text">actuele</strong> cataloguslijst
          in het platform om passende <code className="rounded bg-surface-card px-1">system_id</code>s voor te stellen.
          Na een grote update van de prijsbibliotheek kan het dus zijn dat een oud dossier nog op oudere id&apos;s draait
          terwijl het analysevoorstel nieuwere posten noemt — controleer het voorstel en pas zo nodig handmatig aan, of
          migreer het dossier wanneer daarvoor productondersteuning beschikbaar is.
        </p>

        <div className="mb-8 flex flex-wrap gap-3">
          <Link
            to="/onboarding"
            className="inline-flex rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-alt"
          >
            Volledige introductie
          </Link>
          <Link
            to="/troubleshoot"
            className="inline-flex rounded-lg border border-border bg-surface-card px-4 py-2 text-sm font-medium text-text hover:border-primary"
          >
            Problemen oplossen
          </Link>
          <Link to="/contact" className="inline-flex text-sm font-medium text-primary hover:underline">
            Contact
          </Link>
          <Link
            to="/help/export"
            className="inline-flex rounded-lg border border-border bg-surface-card px-4 py-2 text-sm font-medium text-text hover:border-primary"
          >
            Export &amp; offerte →
          </Link>
        </div>

        <p className="text-xs text-text-muted">
          Technische checklist voor het team: zie <code className="rounded bg-surface px-1">docs/TEKENING_DOCUMENT_FLOW.md</code>{' '}
          in de repository. Rapportage/export: <code className="rounded bg-surface px-1">docs/RAPPORTAGE_EXPORT_FLOW.md</code>.
        </p>
      </AnimatedSection>
    </div>
  );
}
